import { Injectable } from '@angular/core';

/**
 * Projection utilities for MapLibre
 * Replaces Google Maps OverlayView projection methods
 * Converts between geographic coordinates and pixel coordinates
 */
@Injectable({
  providedIn: 'root',
})
export class MapProjectionService {
  /**
   * Calculate pixel coordinates from geographic lat/lng
   * Based on Mercator projection (Web Mercator / EPSG:3857)
   *
   * @param lat Latitude in decimal degrees
   * @param lng Longitude in decimal degrees
   * @param mapCenter Map center point [lng, lat]
   * @param zoom Map zoom level
   * @param mapWidth Map container width in pixels
   * @param mapHeight Map container height in pixels
   * @returns Pixel coordinates [x, y] relative to map container
   */
  latLngToPixel(
    lat: number,
    lng: number,
    mapCenter: [number, number],
    zoom: number,
    mapWidth: number,
    mapHeight: number,
  ): [number, number] {
    const mercatorLat = this.latToMercator(lat);
    const mercatorLng = this.lngToMercator(lng);
    const centerMercatorLat = this.latToMercator(mapCenter[1]);
    const centerMercatorLng = this.lngToMercator(mapCenter[0]);

    // Tiles per side at current zoom
    const tilesPerSide = Math.pow(2, zoom);

    // Calculate position in tile coordinates
    const tileSize = 256;
    const pixelsPerTile = tileSize * (tilesPerSide / 2);

    const pointX = pixelsPerTile * (mercatorLng - centerMercatorLng) + mapWidth / 2;
    const pointY = -pixelsPerTile * (mercatorLat - centerMercatorLat) + mapHeight / 2;

    return [pointX, pointY];
  }

  /**
   * Calculate geographic lat/lng from pixel coordinates
   *
   * @param pixelX Pixel X coordinate
   * @param pixelY Pixel Y coordinate
   * @param mapCenter Map center point [lng, lat]
   * @param zoom Map zoom level
   * @param mapWidth Map container width in pixels
   * @param mapHeight Map container height in pixels
   * @returns Geographic coordinates [lng, lat]
   */
  pixelToLatLng(
    pixelX: number,
    pixelY: number,
    mapCenter: [number, number],
    zoom: number,
    mapWidth: number,
    mapHeight: number,
  ): [number, number] {
    const mercatorLat = this.latToMercator(mapCenter[1]);
    const mercatorLng = this.lngToMercator(mapCenter[0]);

    const tilesPerSide = Math.pow(2, zoom);
    const tileSize = 256;
    const pixelsPerTile = tileSize * (tilesPerSide / 2);

    const mercatorX = (pixelX - mapWidth / 2) / pixelsPerTile + mercatorLng;
    const mercatorY = (mapHeight / 2 - pixelY) / pixelsPerTile + mercatorLat;

    const lat = this.mercatorToLat(mercatorY);
    const lng = this.mercatorToLng(mercatorX);

    return [lng, lat];
  }

  /**
   * Calculate bounds in lat/lng from pixel rectangle
   *
   * @param topLeftPixel Top-left pixel coordinates [x, y]
   * @param bottomRightPixel Bottom-right pixel coordinates [x, y]
   * @param mapCenter Map center [lng, lat]
   * @param zoom Map zoom level
   * @param mapWidth Map width
   * @param mapHeight Map height
   * @returns Bounds object {north, south, east, west}
   */
  pixelRectToLatLngBounds(
    topLeftPixel: [number, number],
    bottomRightPixel: [number, number],
    mapCenter: [number, number],
    zoom: number,
    mapWidth: number,
    mapHeight: number,
  ): { north: number; south: number; east: number; west: number } {
    const [topLeftLng, topLeftLat] = this.pixelToLatLng(
      topLeftPixel[0],
      topLeftPixel[1],
      mapCenter,
      zoom,
      mapWidth,
      mapHeight,
    );

    const [bottomRightLng, bottomRightLat] = this.pixelToLatLng(
      bottomRightPixel[0],
      bottomRightPixel[1],
      mapCenter,
      zoom,
      mapWidth,
      mapHeight,
    );

    return {
      north: Math.max(topLeftLat, bottomRightLat),
      south: Math.min(topLeftLat, bottomRightLat),
      east: Math.max(topLeftLng, bottomRightLng),
      west: Math.min(topLeftLng, bottomRightLng),
    };
  }

  /**
   * Convert latitude to Mercator Y coordinate (0 to 1)
   */
  private latToMercator(lat: number): number {
    const sinLatitude = Math.sin((lat * Math.PI) / 180);
    const y = Math.log((1 + sinLatitude) / (1 - sinLatitude)) / 2;
    return y / (2 * Math.PI) + 0.5;
  }

  /**
   * Convert longitude to Mercator X coordinate (0 to 1)
   */
  private lngToMercator(lng: number): number {
    return lng / 360 + 0.5;
  }

  /**
   * Convert Mercator Y coordinate to latitude
   */
  private mercatorToLat(y: number): number {
    const e = 2.718281828459045;
    const latitude = 2 * Math.atan(e ** ((y - 0.5) * 2 * Math.PI)) - Math.PI / 2;
    return (latitude * 180) / Math.PI;
  }

  /**
   * Convert Mercator X coordinate to longitude
   */
  private mercatorToLng(x: number): number {
    return (x - 0.5) * 360;
  }

  /**
   * Calculate great-circle distance between two points
   * using Haversine formula
   *
   * @param lat1 Latitude 1
   * @param lng1 Longitude 1
   * @param lat2 Latitude 2
   * @param lng2 Longitude 2
   * @returns Distance in kilometers
   */
  distanceBetweenPoints(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const EARTH_RADIUS_KM = 6371;
    const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);

    const deltaLatitude = degreesToRadians(lat2 - lat1);
    const deltaLongitude = degreesToRadians(lng2 - lng1);

    const a =
      Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
      Math.cos(degreesToRadians(lat1)) *
        Math.cos(degreesToRadians(lat2)) *
        Math.sin(deltaLongitude / 2) *
        Math.sin(deltaLongitude / 2);

    const angularDistance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_KM * angularDistance;
  }

  /**
   * Calculate destination point given start point, bearing, and distance
   * Uses spherical geometry (great-circle calculation)
   *
   * @param lat Starting latitude
   * @param lng Starting longitude
   * @param distance Distance in meters
   * @param bearing Bearing in degrees (0-360, where 0=North, 90=East)
   * @returns Destination [lng, lat]
   */
  computeOffset(
    lat: number,
    lng: number,
    distance: number,
    bearing: number,
  ): [number, number] {
    const EARTH_RADIUS_M = 6371000; // Earth's radius in meters

    const d = distance / EARTH_RADIUS_M; // Angular distance in radians
    const brng = (bearing * Math.PI) / 180; // Bearing in radians
    const lat1Rad = (lat * Math.PI) / 180;
    const lng1Rad = (lng * Math.PI) / 180;

    const lat2Rad = Math.asin(
      Math.sin(lat1Rad) * Math.cos(d) +
        Math.cos(lat1Rad) * Math.sin(d) * Math.cos(brng),
    );

    const lng2Rad =
      lng1Rad +
      Math.atan2(
        Math.sin(brng) * Math.sin(d) * Math.cos(lat1Rad),
        Math.cos(d) - Math.sin(lat1Rad) * Math.sin(lat2Rad),
      );

    const lat2 = (lat2Rad * 180) / Math.PI;
    const lng2 = (lng2Rad * 180) / Math.PI;

    return [lng2, lat2];
  }

  /**
   * Get bounds that fit all provided coordinates
   *
   * @param coordinates Array of [lng, lat] pairs
   * @returns Bounds {north, south, east, west}
   */
  getBounds(coordinates: [number, number][]): {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null {
    if (coordinates.length === 0) return null;

    let north = coordinates[0][1];
    let south = coordinates[0][1];
    let east = coordinates[0][0];
    let west = coordinates[0][0];

    for (const [lng, lat] of coordinates) {
      north = Math.max(north, lat);
      south = Math.min(south, lat);
      east = Math.max(east, lng);
      west = Math.min(west, lng);
    }

    return { north, south, east, west };
  }
}
